import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fieldHelpAttributes, type FieldHelpProps } from '@/lib/fieldGuidance';

const fieldClass = 'kiq-field w-full min-w-0 border px-3.5 py-2.5 text-sm outline-none';
const unstyledInputTypes = new Set([
  'checkbox',
  'radio',
  'hidden',
  'range',
  'color',
  'button',
  'submit',
  'reset',
  'image',
]);

/** Native inputs retain their refs, validation, selection and number semantics. */
export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & FieldHelpProps
>(function Input({ className, type = 'text', help, helpLabel, ...props }, ref) {
  return (
    <input
      {...props}
      {...fieldHelpAttributes(
        { ...props, type, label: helpLabel ?? props['aria-label'] },
        help ?? props['aria-description'],
      )}
      aria-label={props['aria-label'] ?? (props['aria-labelledby'] ? undefined : helpLabel)}
      ref={ref}
      type={type}
      className={cn(!unstyledInputTypes.has(type) && fieldClass, className)}
    />
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & FieldHelpProps
>(function Textarea({ className, help, helpLabel, ...props }, ref) {
  return (
    <textarea
      {...props}
      {...fieldHelpAttributes(
        { ...props, type: 'textarea', label: helpLabel ?? props['aria-label'] },
        help ?? props['aria-description'],
      )}
      aria-label={props['aria-label'] ?? (props['aria-labelledby'] ? undefined : helpLabel)}
      ref={ref}
      className={cn(fieldClass, 'kiq-textarea', className)}
    />
  );
});

export interface SelectChangeEvent {
  type: 'change';
  target: { name: string; value: string };
  currentTarget: { name: string; value: string };
}

interface SelectProps
  extends
    FieldHelpProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'defaultValue' | 'onChange' | 'type'> {
  value?: string | number | null;
  defaultValue?: string | number;
  onChange?: (event: SelectChangeEvent) => void;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
}

interface Option {
  value: string;
  label: string;
  disabled: boolean;
  group?: string;
}

function optionText(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      if (isValidElement<{ children?: ReactNode }>(child)) return optionText(child.props.children);
      return '';
    })
    .join('');
}

function collectOptions(children: ReactNode, group?: string, groupDisabled = false): Option[] {
  const options: Option[] = [];
  Children.forEach(children, (child) => {
    if (
      !isValidElement<{
        value?: string | number;
        children?: ReactNode;
        disabled?: boolean;
        label?: string;
      }>(child)
    )
      return;
    if (child.type === 'option') {
      const label = child.props.label ?? optionText(child.props.children);
      options.push({
        value: String(child.props.value ?? label),
        label,
        disabled: groupDisabled || !!child.props.disabled,
        group,
      });
    } else if (child.type === Fragment || child.type === 'optgroup') {
      options.push(
        ...collectOptions(
          child.props.children,
          child.type === 'optgroup' ? child.props.label : group,
          groupDisabled || !!child.props.disabled,
        ),
      );
    }
  });
  return options;
}

/**
 * Styled Radix select with native string values, including a real empty option.
 * Form fields use Controller: the forwarded ref focuses the visible trigger,
 * and value/reset/touched state stay owned by React Hook Form.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    children,
    value,
    defaultValue,
    onChange,
    name,
    required,
    autoComplete,
    form,
    disabled,
    className,
    placeholder = 'Choose an option',
    help,
    helpLabel,
    ...triggerProps
  },
  ref,
) {
  const options = collectOptions(children);
  const [internalValue, setInternalValue] = useState(() =>
    String(defaultValue ?? options.find((o) => !o.disabled)?.value ?? ''),
  );
  const selectedValue = value === undefined ? internalValue : value === null ? '' : String(value);
  const selected = options.find((option) => option.value === selectedValue);
  const change = (next: string) => {
    if (value === undefined) setInternalValue(next);
    const target = { name: name ?? '', value: next };
    onChange?.({ type: 'change', target, currentTarget: target });
  };
  const empty = options.find((option) => option.value === '');
  const groups: { label?: string; options: Option[] }[] = [];
  for (const option of options) {
    const group = groups[groups.length - 1];
    if (group && group.label === option.group) group.options.push(option);
    else groups.push({ label: option.group, options: [option] });
  }
  const renderOption = (option: Option, index: number) => (
    <SelectPrimitive.Item
      key={`${option.value}-${index}`}
      value={option.value}
      disabled={option.disabled}
      textValue={option.label}
      className="kiq-select-option"
    >
      <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="ml-auto shrink-0 text-primary">
        <Check className="h-4 w-4" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );

  return (
    <SelectPrimitive.Root
      value={selectedValue}
      onValueChange={change}
      name={name}
      required={required}
      disabled={disabled}
      autoComplete={autoComplete}
      form={form}
    >
      <SelectPrimitive.Trigger
        {...triggerProps}
        {...fieldHelpAttributes(
          { ...triggerProps, name, type: 'select', label: helpLabel ?? triggerProps['aria-label'] },
          help ?? triggerProps['aria-description'],
        )}
        aria-label={
          triggerProps['aria-label'] ?? (triggerProps['aria-labelledby'] ? undefined : helpLabel)
        }
        ref={ref}
        disabled={disabled}
        aria-required={required || undefined}
        data-empty={selectedValue === '' || undefined}
        className={cn(
          fieldClass,
          'kiq-select-trigger inline-flex w-auto max-w-full items-center justify-between gap-2 text-left',
          className,
        )}
      >
        <span className="min-w-0 flex-1 truncate">
          <SelectPrimitive.Value placeholder={empty?.label ?? placeholder}>
            {selected?.label ?? placeholder}
          </SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="kiq-select-chevron h-4 w-4 shrink-0" aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="kiq-select-content"
        >
          <SelectPrimitive.ScrollUpButton className="kiq-select-scroll">
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="kiq-select-viewport">
            {options.length === 0 && (
              <div role="status" className="px-3 py-5 text-center text-sm text-muted-foreground">
                No options available
              </div>
            )}
            {groups.map((group, index) =>
              group.label ? (
                <SelectPrimitive.Group key={`${group.label}-${index}`}>
                  <SelectPrimitive.Label className="px-3 pb-1 pt-3 text-xs font-semibold text-muted-foreground">
                    {group.label}
                  </SelectPrimitive.Label>
                  {group.options.map(renderOption)}
                </SelectPrimitive.Group>
              ) : (
                <Fragment key={index}>{group.options.map(renderOption)}</Fragment>
              ),
            )}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="kiq-select-scroll">
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});
